/* Suggested rules — built to screen 36 of the Claude Design reference (`Lumiq App.dc.html`).
 *
 * **This is a rebuild, and the reason is worth recording: the previous version was built from the
 * wrong file.** The ZIP carries two exports of the same design — `Lumiq App standalone-src.dc.html`
 * with 38 screens, and `Lumiq App.dc.html` with 43 — and only the second contains this screen in
 * its finished form. The older one is a flat list of claim sentences with a "Draft as rule" action;
 * the authoritative one is a **table of six columns inside a per-tier card**, with a dataset picker
 * that swaps for the chosen dataset's name, five summary tiles, an eight-step lifecycle panel, a
 * form preview and a collapsible boundaries panel. Reading the wrong export produced a screen that
 * was internally consistent and wrong throughout, so the file identity is named here rather than
 * left to be rediscovered.
 *
 * Four things the reference is deliberate about, all of which this screen holds.
 *
 * **The AI status is the authority, not the list.** A model row appears only when the run's status
 * is `contributed` — `visibleSuggestions` enforces it, and the reference's own comment says why:
 * filtering by tier alone "would let a run reporting a rejected answer still show model rows if one
 * survived serialisation". Our stored rows and our status come from different statements, so that
 * run is representable. Where the AI tier would be, the status chip and its sentence stand in.
 *
 * **The evidence tier is a 3px rule, never a tinted card.** Declared, inferred and model-proposed
 * take one colour each on the left edge of their card header, plus a chip whose dot is *filled* for
 * the two deterministic tiers and a hollow ring for the model one — so the distinction survives
 * greyscale rather than resting on hue.
 *
 * **An AI row is annotated inside a table that otherwise looks identical.** Three additions the
 * reference makes and this screen makes: the parameters cell says a person supplies the values, the
 * Why cell says Lumiq wrote the sentence, and the action carries a "requires review" stamp. That is
 * how a model proposal stays visibly weaker evidence without being visually louder.
 *
 * **Every state has its own words.** Four empty states (no dataset, never generated, thin metadata,
 * nothing justified), a failure band that keeps the last good result visible, and a skeleton for
 * both the tiles and the rows. None reads as a broken feature, which is the reference's stated aim
 * and why it writes four sentences where one would have fitted.
 *
 * What this screen does **not** carry, because the reference does not: a composed claim sentence
 * per row, a `· dataset` suffix on every row, and the closing "there is no accept route here"
 * footer. All three came from the older export or from the previous build.
 */

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDatasetNames } from '@/features/catalog-browser';
import { SCREEN_PATHS } from '@/shared/routes';
import type { RulePrefill } from '@/shared/routes';

import {
  useRegenerateSuggestions,
  useRulegenCapabilities,
  useSuggestions,
} from '../api/rulegen';
import type { CandidateSource, GenerationOutcome, Suggestion } from '../api/rulegen';
import {
  FLOW,
  aiPresentation,
  groupedByTier,
  parameterText,
  presentedStatus,
  showsAiStandIn,
  tierDotIsFilled,
  tierKey,
  tierModifier,
  tilesFor,
  visibleSuggestions,
} from '../model/presentation';

/** A status chip: a ring-or-filled dot, then the label. The reference's shape, in two sizes. */
function Chip({
  tone,
  filled,
  label,
  title,
  small,
}: {
  readonly tone: string;
  readonly filled: boolean;
  readonly label: string;
  readonly title: string;
  readonly small?: boolean;
}): ReactElement {
  return (
    <span
      className={`lq-sg__chip lq-sg__chip--${tone}${small === true ? ' lq-sg__chip--sm' : ''}`}
      title={title}
    >
      <span className={`lq-sg__dot${filled ? ' lq-sg__dot--filled' : ''}`} />
      {label}
    </span>
  );
}

/** The five tiles: four figures and the AI status. */
function Summary({
  outcome,
  visible,
}: {
  readonly outcome: GenerationOutcome;
  readonly visible: readonly Suggestion[];
}): ReactElement {
  const { t } = useTranslation();
  const deterministic = visible.filter((item) => item.source !== 'model_proposed').length;
  const ai = aiPresentation(presentedStatus(outcome.model_status, deterministic));

  return (
    <div className="lq-sg__tiles">
      {tilesFor(outcome, visible).map((tile) => (
        <div className="lq-sg__tile" key={tile.label}>
          <div
            className={`lq-sg__tile-value${
              tile.modifier === '' ? '' : ` lq-sg__tile-value--${tile.modifier}`
            }`}
          >
            {tile.value}
          </div>
          <div className="lq-sg__eyebrow lq-sg__tile-label">{t(tile.label)}</div>
          <div className="lq-sg__note lq-sg__tile-hint">
            {t(tile.hint, tile.hintValues ?? {})}
          </div>
        </div>
      ))}

      {/* The AI status as a tile rather than a banner, so it sits *beside* the figures it
          qualifies. Its 3px top rule is the only place on this screen AI gets a visual of its own,
          and a rule rather than a tint keeps it from outweighing the four counts. */}
      <div
        className={`lq-sg__ai-tile${ai.tone === 'ok' ? ' lq-sg__ai-tile--ok' : ''}${
          ai.tone === 'warn' ? ' lq-sg__ai-tile--warn' : ''
        }`}
      >
        <div className="lq-sg__eyebrow lq-sg__tile-label" style={{ marginTop: 0 }}>
          {t('suggestions.ai.title')}
        </div>
        <Chip
          filled={ai.filled}
          label={t(ai.key)}
          title={t(`${ai.key}.note`)}
          tone={ai.tone}
        />
        <div className="lq-sg__ai-note">{t(`${ai.key}.note`)}</div>
      </div>
    </div>
  );
}

/** One evidence tier: the reference's card header, then the six-column table. */
function Tier({
  source,
  items,
  onCreate,
}: {
  readonly source: CandidateSource;
  readonly items: readonly Suggestion[];
  readonly onCreate: (suggestion: Suggestion) => void;
}): ReactElement {
  const { t } = useTranslation();
  const stem = tierKey(source);
  const modifier = tierModifier(source);

  return (
    <div className="lq-sg__panel">
      <div className={`lq-sg__strip lq-sg__tier lq-sg__tier--${modifier}`}>
        <span className="lq-sg__eyebrow lq-sg__tier-title">{t(stem)}</span>
        <Chip
          filled={tierDotIsFilled(source)}
          label={t(`${stem}.badge`)}
          small
          title={t(`${stem}.meaning`)}
          tone={modifier === 'declared' ? 'ok' : modifier === 'inferred' ? 'info' : 'ai'}
        />
        <span className="lq-sg__tier-sub">{t(`${stem}.subtitle`)}</span>
        <span className="lq-sg__tier-count">
          {t('suggestions.group.count', { count: items.length })}
        </span>
      </div>

      <div className="lq-sg__scroll">
        <table className="lq-sg__table">
          <thead>
            <tr>
              <th scope="col">{t('suggestions.column.kind')}</th>
              <th scope="col">{t('suggestions.column.columns')}</th>
              <th scope="col">{t('suggestions.column.parameters')}</th>
              <th scope="col">{t('suggestions.column.rationale')}</th>
              <th className="lq-sg__num" scope="col">
                {t('suggestions.column.checks')}
              </th>
              <th className="lq-sg__num" scope="col">
                {t('suggestions.column.act')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const isAi = item.source === 'model_proposed';
              return (
                /* Keyed on the server's own id, which migration 0093 added for exactly this. */
                <tr key={item.id}>
                  <td className="lq-sg__mono lq-sg__mono--nowrap">{item.kind}</td>
                  <td className="lq-sg__mono">{item.columns.join(', ')}</td>
                  <td>
                    <span className="lq-sg__param">{parameterText(item.parameters)}</span>
                    {/* Only on a model row. The parameters a model may propose are structural —
                        the content-bearing keys are refused by the validator — and this says so
                        where a reviewer is looking at them. */}
                    {isAi && (
                      <span className="lq-sg__aside">
                        {t('suggestions.row.youSupply')}
                      </span>
                    )}
                  </td>
                  <td className="lq-sg__why">
                    {/* Host-written for every source, including a model candidate: carrying a
                        model's own rationale would put unvalidated model text on a screen. The
                        aside states that on the rows where a reader would wonder. */}
                    <span>{item.rationale}</span>
                    {isAi && (
                      <span className="lq-sg__aside">
                        {t('suggestions.row.hostWrote')}
                      </span>
                    )}
                  </td>
                  <td className="lq-sg__num">
                    <span
                      className="lq-sg__checks"
                      title={t('suggestions.row.checkList')}
                    >
                      {item.checks.length}
                    </span>
                  </td>
                  <td className="lq-sg__num">
                    {/* **"Create rule", the reference's own label — never "Accept".** It opens the
                        authoring form with these values filled in and creates nothing; the rule
                        exists when a person submits, and is then a draft that still needs
                        publishing and a two-eyes activation.

                        Carries only the suggestion's id: the fields are re-read on the other side
                        from `GET .../rule-draft`, so a reload or a shared link resolves to the
                        same values rather than to an empty form. */}
                    <button
                      className="lq-sg__act"
                      onClick={() => {
                        onCreate(item);
                      }}
                      title={t('suggestions.act.hint')}
                      type="button"
                    >
                      {t('suggestions.act.create')}
                    </button>
                    {isAi && (
                      <span className="lq-sg__review">{t('suggestions.act.review')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The lifecycle panel and the form preview, side by side. */
function AfterCreate(): ReactElement {
  const { t } = useTranslation();
  const fields = [
    { label: 'suggestions.form.name', value: 'customer_id is never null', mono: false },
    { label: 'suggestions.form.kind', value: 'not_null', mono: true },
    { label: 'suggestions.form.columns', value: 'customer_id', mono: true },
    {
      label: 'suggestions.form.owner',
      value: t('suggestions.form.owner.value'),
      mono: false,
    },
  ];

  return (
    <div className="lq-sg__after">
      <div className="lq-sg__panel lq-sg__panel--pad lq-sg__after-flow">
        <div className="lq-sg__eyebrow" style={{ marginBottom: 'var(--space-1)' }}>
          {t('suggestions.after.title')}
        </div>
        <div className="lq-sg__note lq-sg__after-lede">{t('suggestions.after.body')}</div>
        {/* Eight steps, and the numbering is information rather than decoration: this is a real
            sequence, and *which step a suggestion stops at* is the screen's safety claim. Only 02
            and 08 are marked, which is what makes the six between them read as the review nobody
            may skip. */}
        <div className="lq-sg__flow">
          {FLOW.map((step) => (
            <div
              className={`lq-sg__step${step.mark === '' ? '' : ` lq-sg__step--${step.mark}`}`}
              key={step.n}
            >
              <div className="lq-sg__step-n">{step.n}</div>
              <div className="lq-sg__step-label">{t(step.label)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="lq-sg__panel lq-sg__panel--pad lq-sg__after-form">
        <div className="lq-sg__eyebrow" style={{ marginBottom: 'var(--space-3)' }}>
          {t('suggestions.form.title')}
        </div>
        <div className="lq-sg__banner">{t('suggestions.form.banner')}</div>
        {fields.map((field) => (
          <div className="lq-sg__field" key={field.label}>
            <div className="lq-sg__eyebrow">{t(field.label)}</div>
            <div
              className={`lq-sg__field-value${
                field.mono ? ' lq-sg__field-value--mono' : ''
              }`}
            >
              {field.value}
            </div>
          </div>
        ))}
        <div className="lq-sg__note lq-sg__foot">{t('suggestions.form.footnote')}</div>
      </div>
    </div>
  );
}

/** The collapsible boundaries panel. Collapsed by default, as the reference has it. */
function Boundaries(): ReactElement | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);
  const capabilities = useRulegenCapabilities();

  if (!capabilities.isSuccess) return null;
  const { proposable_kinds, never_proposed, checks, authorization_policy } =
    capabilities.data;

  return (
    <div className="lq-sg__panel" style={{ marginBottom: 0 }}>
      <div className="lq-sg__strip lq-sg__strip--plain">
        <div className="lq-sg__bounds-main">
          <div className="lq-sg__eyebrow lq-sg__bounds-title">
            {t('suggestions.boundaries.title')}
          </div>
          <div className="lq-sg__note">{t('suggestions.boundaries.hint')}</div>
        </div>
        <a
          className="lq-sg__chosen-swap"
          href="#"
          onClick={(event) => {
            event.preventDefault();
            setOpen((current) => !current);
          }}
        >
          {t(open ? 'suggestions.boundaries.collapse' : 'suggestions.boundaries.expand')}
        </a>
      </div>

      {!open && (
        <div className="lq-sg__bounds-summary">{t('suggestions.boundaries.summary')}</div>
      )}

      {open && (
        <div className="lq-sg__bounds-open">
          <div className="lq-sg__bounds-lead">{t('suggestions.boundaries.lead')}</div>
          {/* The server's own sentence, rendered rather than paraphrased. The bound is the
              *tenant*, and a screen restating a security control more strongly than the code
              enforces it is this repository's most-repeated defect — carrying the field means the
              claim cannot drift from the mechanism. */}
          <div className="lq-sg__bounds-body">{authorization_policy}</div>

          <div className="lq-sg__eyebrow lq-sg__bounds-heading">
            {t('suggestions.boundaries.proposes')}
          </div>
          <div className="lq-sg__kinds">
            {proposable_kinds.map((kind) => (
              <span className="lq-sg__kind" key={kind}>
                {kind}
              </span>
            ))}
          </div>

          <div className="lq-sg__eyebrow lq-sg__bounds-heading">
            {t('suggestions.boundaries.never')}
          </div>
          {/* Neutral rows with a strong left rule, **not** amber alerts. The previous build made
              these three warning banners, which reads as "something is wrong here"; a refusal by
              policy is a stated boundary rather than a fault. */}
          {Object.entries(never_proposed).map(([kind, reason]) => (
            <div className="lq-sg__never" key={kind}>
              <span className="lq-sg__never-kind">{kind}</span>
              <span className="lq-sg__never-why">{reason}</span>
            </div>
          ))}

          <div className="lq-sg__checks-toggle">
            <a
              className="lq-sg__chosen-swap"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                setChecksOpen((current) => !current);
              }}
            >
              {t('suggestions.boundaries.showChecks', {
                shown: checksOpen ? 'yes' : 'no',
                count: checks.length,
              })}
            </a>
            {checksOpen && (
              <div className="lq-sg__checklist">
                {checks.map((check) => (
                  <div className="lq-sg__check" key={check}>
                    <span className="lq-sg__check-mark" />
                    <span className="lq-sg__check-text">{check}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function SuggestionsScreen(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const names = useDatasetNames();
  const [datasetId, setDatasetId] = useState('');
  const suggestions = useSuggestions(datasetId === '' ? undefined : datasetId);
  const regenerate = useRegenerateSuggestions();

  /* The outcome from the regeneration *this session* performed, and nothing else. The GET returns
     only the suggestion list — by design, because a run's counts describe a run rather than a
     dataset's current state, and serving them from the list would attribute one generation's
     numbers to whatever the list holds now. */
  const outcome = regenerate.data?.dataset_id === datasetId ? regenerate.data : undefined;
  const busy = regenerate.isPending && regenerate.variables === datasetId;
  const failed = regenerate.isError && regenerate.variables === datasetId;
  const hasRun = outcome !== undefined;

  const options = [
    { value: '', label: t('suggestions.pick.label') },
    /* `new Map<string, string>()` and not a bare `new Map()`: an untyped empty map widens the
       destructured entries to `any`, which `no-unsafe-assignment` catches — the lint rule doing
       its job, since the widening would silence a real type error in the `.map` below. */
    ...[...(names.data?.byId ?? new Map<string, string>())]
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((left, right) => left.label.localeCompare(right.label)),
  ];
  const datasetName = names.data?.byId.get(datasetId);

  /* Choosing a different dataset drops the previous dataset's failure. Without this a 409 saying
     "no columns of *this dataset* are visible to you" stays on screen beside the newly chosen
     table — the one message the backend deliberately frames as a permissions answer,
     misattributed to a dataset that is fine. */
  const reset = regenerate.reset;
  useEffect(() => {
    reset();
  }, [datasetId, reset]);

  const stored = suggestions.data?.suggestions ?? [];
  const status = outcome?.model_status ?? 'not_configured';
  /* **Status first, tier second.** A model row is shown only on a run whose status is
     `contributed` — see `visibleSuggestions`, which is the reference's own invariant. */
  const visible = visibleSuggestions(stored, status);
  const groups = groupedByTier(visible);
  const deterministic = visible.filter((item) => item.source !== 'model_proposed').length;
  const presented = presentedStatus(status, deterministic);
  const ai = aiPresentation(presented);
  const nothingFound = hasRun && !busy && visible.length === 0;

  return (
    <div className="lq-sg">
      <div className="lq-sg__head">
        <h1 className="lq-sg__title">{t('suggestions.title')}</h1>
        <p className="lq-sg__lede">{t('suggestions.lede')}</p>
      </div>
      <div className="lq-sg__rule" />

      {/* ------------------------------------------------------------- the dataset picker */}
      <div className="lq-sg__panel">
        <div className="lq-sg__strip">
          <span className="lq-sg__eyebrow" style={{ flex: 1 }}>
            {t('suggestions.pick.title')}
          </span>
          <span className="lq-sg__note">{t('suggestions.pick.hint')}</span>
        </div>
        <div className="lq-sg__body">
          <div className="lq-sg__pick">
            {/* The reference swaps the control for the *answer* once a dataset is chosen, which is
                what makes the page read as being about that dataset rather than about picking one.
                Both states are the reference's. */}
            {datasetId === '' ? (
              <div className="lq-sg__pick-field">
                <label className="lq-sg__eyebrow lq-sg__label" htmlFor="lq-sg-dataset">
                  {t('suggestions.pick.label')}
                </label>
                <select
                  className="lq-sg__select"
                  id="lq-sg-dataset"
                  onChange={(event) => {
                    setDatasetId(event.target.value);
                  }}
                  value={datasetId}
                >
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="lq-sg__pick-field">
                <div className="lq-sg__eyebrow lq-sg__label">
                  {t('suggestions.pick.label')}
                </div>
                <div className="lq-sg__chosen">
                  <span className="lq-sg__chosen-name">{datasetName ?? datasetId}</span>
                  <a
                    className="lq-sg__chosen-swap"
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setDatasetId('');
                    }}
                  >
                    {t('suggestions.pick.swap')}
                  </a>
                </div>
                {/* `sg_ds_meta`. The reference also appends a last-generated time; this platform
                    stores none, so only the half it actually knows is rendered rather than an
                    invented timestamp. */}
                {hasRun && (
                  <div className="lq-sg__note lq-sg__meta">
                    {t('suggestions.pick.meta', { count: outcome.columns_considered })}
                  </div>
                )}
              </div>
            )}

            <button
              className="lq-sg__go"
              disabled={datasetId === '' || busy}
              onClick={() => {
                regenerate.mutate(datasetId);
              }}
              title={
                datasetId === ''
                  ? t('suggestions.pick.first')
                  : t('suggestions.pick.replaces')
              }
              type="button"
            >
              {busy && <span className="lq-sg__spinner" />}
              <span>
                {t(
                  busy
                    ? 'suggestions.generating'
                    : hasRun
                      ? 'suggestions.generateAgain'
                      : 'suggestions.generate',
                )}
              </span>
            </button>
          </div>
          <div className="lq-sg__note lq-sg__foot">{t('suggestions.pick.footnote')}</div>
        </div>
      </div>

      {/* ------------------------------------------------------------- generation failed */}
      {failed && (
        <>
          <div className="lq-sg__fail">
            <div className="lq-sg__fail-main">
              <div className="lq-sg__fail-title">
                {t('suggestions.fail.title', { dataset: datasetName ?? datasetId })}
              </div>
              <div className="lq-sg__fail-text">{regenerate.error.message}</div>
            </div>
            <button
              className="lq-sg__act"
              onClick={() => {
                regenerate.mutate(datasetId);
              }}
              type="button"
            >
              {t('suggestions.fail.retry')}
            </button>
          </div>
          {/* A failed refresh must not erase a result that worked — the reference says so, and this
              is where it is true: the rows below come from the listing, which a failed mutation
              does not invalidate. */}
          {visible.length > 0 && (
            <div className="lq-sg__note lq-sg__kept">{t('suggestions.fail.kept')}</div>
          )}
        </>
      )}

      {/* ------------------------------------------------------------- loading */}
      {busy && (
        <>
          <div className="lq-sg__tiles">
            {[0, 1, 2, 3, 4].map((slot) => (
              <div className="lq-sg__tile" key={slot}>
                <div className="lq-sg__skel lq-sg__skel-tile-value" />
                <div className="lq-sg__skel lq-sg__skel-tile-label" />
              </div>
            ))}
          </div>
          <div className="lq-sg__panel">
            {[0, 1, 2, 3, 4, 5].map((slot) => (
              <div className="lq-sg__skel-row" key={slot}>
                <div className="lq-sg__skel lq-sg__skel-bar lq-sg__skel-bar--kind" />
                <div className="lq-sg__skel lq-sg__skel-bar lq-sg__skel-bar--cols" />
                <div className="lq-sg__skel lq-sg__skel-bar lq-sg__skel-bar--why" />
              </div>
            ))}
          </div>
        </>
      )}

      {/* ------------------------------------------------------------- the run's figures */}
      {!busy && hasRun && <Summary outcome={outcome} visible={visible} />}

      {/* ------------------------------------------------------------- the tiers */}
      {!busy &&
        groups.map((group) => (
          <Tier
            items={group.items}
            key={group.source}
            onCreate={(item) => {
              navigate(SCREEN_PATHS.rules, {
                state: { suggestionId: item.id } satisfies RulePrefill,
              });
            }}
            source={group.source}
          />
        ))}

      {/* Where the AI tier would be: the status chip and its sentence. Never an empty heading, and
          never AI wording on a result no model touched. */}
      {!busy && hasRun && showsAiStandIn(presented, visible.length) && (
        <div
          className={`lq-sg__ai-absent${
            ai.tone === 'warn' ? ' lq-sg__ai-absent--warn' : ''
          }`}
        >
          <div className="lq-sg__ai-absent-row">
            <Chip
              filled={ai.filled}
              label={t(ai.key)}
              title={t(`${ai.key}.note`)}
              tone={ai.tone}
            />
            <span className="lq-sg__ai-absent-note">{t(`${ai.key}.note`)}</span>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- the four empty states */}
      {datasetId === '' && (
        <div className="lq-sg__empty">
          <div className="lq-sg__empty-title">{t('suggestions.empty.blank.title')}</div>
          <div className="lq-sg__empty-body">{t('suggestions.empty.blank.body')}</div>
        </div>
      )}

      {datasetId !== '' && !busy && !hasRun && !failed && (
        <div className="lq-sg__empty">
          <div className="lq-sg__empty-title">
            {t('suggestions.empty.never.title', { dataset: datasetName ?? datasetId })}
          </div>
          <div className="lq-sg__empty-body lq-sg__empty-body--narrow">
            {t('suggestions.empty.never.body')}
          </div>
        </div>
      )}

      {/* Thin and none are different answers and the reference separates them. A
          `columns_considered` of zero is the only honest reading of "not enough metadata" this
          platform can give: the catalogue holds the dataset and nothing about its columns.
          Above zero and still empty is the other answer — every column was looked at and none
          justified a rule. */}
      {nothingFound && (
        <div
          className={
            outcome.columns_considered === 0
              ? 'lq-sg__empty lq-sg__empty--thin'
              : 'lq-sg__empty lq-sg__empty--answer'
          }
        >
          <div className="lq-sg__empty-title">
            {t(
              outcome.columns_considered === 0
                ? 'suggestions.empty.thin.title'
                : 'suggestions.empty.none.title',
            )}
          </div>
          <div className="lq-sg__empty-body lq-sg__empty-body--wide">
            {t(
              outcome.columns_considered === 0
                ? 'suggestions.empty.thin.body'
                : 'suggestions.empty.none.body',
              { count: outcome.columns_considered },
            )}
          </div>
        </div>
      )}

      <AfterCreate />
      <Boundaries />
    </div>
  );
}
